/**
 * Authentication utilities using Clerk.
 *
 * Roles read from Clerk `publicMetadata.role`:
 *   - "super_admin"  → full access (revenue, system, billing/suscripciones)
 *   - "admin"        → content + commerce except sensitive financial/system pages
 *   - anything else  → "user" (no admin access)
 *
 * VIP membership/registro flow (CompleteRegistration / PendingApproval / MiAcceso):
 *   1. user.unsafeMetadata.{alias, telegram, birthDate}  (CompleteRegistration)
 *   2. user.publicMetadata.approved === true             (admin approve)
 */

import type { UserResource } from "@clerk/types";
import { useUser } from "@clerk/clerk-react";

export type UserRole = "super_admin" | "admin" | "user";

export const MIN_REGISTRATION_AGE = 18;

export interface RegistrationData {
  alias: string;
  birthDate: string; // ISO o YYYY-MM-DD
  telegram: string;
}

/** Highest privilege first → lowest. Useful for hierarchical comparisons. */
const ROLE_RANK: Record<UserRole, number> = {
  super_admin: 2,
  admin: 1,
  user: 0,
};

function normalizeRole(value: unknown): UserRole {
  if (value === "super_admin" || value === "superadmin" || value === "super-admin") {
    return "super_admin";
  }
  if (value === "admin") return "admin";
  return "user";
}

/** Exported for geo-sim / role gates that read raw metadata. */
export function normalizeRoleFromUnknown(value: unknown): UserRole {
  return normalizeRole(value);
}

/** True si publicMetadata otorga permisos de panel/API admin (alineado con api/lib/clerkAuth). */
export function isPublicMetadataAdmin(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== "object") return false;
  const role = meta.role;
  if (role === "admin" || role === "super_admin") return true;
  return meta.super_admin === true || meta.is_super_admin === true;
}

/**
 * Get the current user's role from Clerk public metadata.
 * Until useUser is loaded, treats role as "user" (see ProtectedAdminRoute userLoaded gate).
 */
export function useUserRole(): UserRole {
  const { isLoaded, user } = useUser();
  if (!isLoaded) return "user";
  return normalizeRole(user?.publicMetadata?.role);
}

/** Any admin (admin or super_admin). Used to gate the whole /admin section. */
export function useIsAdmin(): boolean {
  const role = useUserRole();
  return ROLE_RANK[role] >= ROLE_RANK.admin;
}

/** Only super admins. Used to gate sensitive pages (revenue, system, billing). */
export function useIsSuperAdmin(): boolean {
  const { isLoaded, user } = useUser();
  if (!isLoaded || !user) return false;
  if (normalizeRole(user.publicMetadata?.role) === "super_admin") return true;
  const meta = (user.publicMetadata ?? {}) as Record<string, unknown>;
  return meta.super_admin === true || meta.is_super_admin === true;
}

/** True when the current role meets or exceeds the required one. */
export function useHasRole(required: UserRole): boolean {
  const role = useUserRole();
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

/** Admin check on a Clerk user resource (no hook). */
export function isClerkAdmin(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  return isPublicMetadataAdmin(user.publicMetadata as Record<string, unknown>);
}

// ─── Profile completion (registro VIP) ────────────────────────────────────────

/**
 * Returns true cuando el user tiene los campos del CompleteRegistration:
 * alias, birthDate, telegram y terms_accepted_at en unsafeMetadata. Admins → siempre "completos".
 */
export function isProfileComplete(
  user: UserResource | null | undefined,
): boolean {
  if (!user) return false;
  if (isClerkAdmin(user)) return true;
  const meta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
  const alias = typeof meta.alias === "string" ? meta.alias.trim() : "";
  const birthDate =
    typeof meta.birthDate === "string" ? meta.birthDate.trim() : "";
  const telegram = typeof meta.telegram === "string" ? meta.telegram.trim() : "";
  const termsAcceptedAt = typeof meta.terms_accepted_at === "string" ? meta.terms_accepted_at.trim() : "";
  return alias.length >= 3 && birthDate.length >= 4 && telegram.length >= 3 && termsAcceptedAt.length > 0;
}

export function getRegistrationData(
  user: UserResource | null | undefined,
): RegistrationData {
  const meta = (user?.unsafeMetadata ?? {}) as Record<string, unknown>;
  return {
    alias: typeof meta.alias === "string" ? meta.alias : "",
    birthDate: typeof meta.birthDate === "string" ? meta.birthDate : "",
    telegram: typeof meta.telegram === "string" ? meta.telegram : "",
  };
}

// ─── Validation ───────────────────────────────────────────────────────────────

export function isAdultBirthDate(dateIsoOrYmd: string): boolean {
  if (!dateIsoOrYmd) return false;
  const parsed = new Date(
    dateIsoOrYmd.length === 10 ? `${dateIsoOrYmd}T00:00:00.000Z` : dateIsoOrYmd,
  );
  if (Number.isNaN(parsed.getTime())) return false;
  const now = new Date();
  let age = now.getFullYear() - parsed.getFullYear();
  const m = now.getMonth() - parsed.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < parsed.getDate())) age -= 1;
  return age >= MIN_REGISTRATION_AGE;
}

/** Idiomas soportados por las validaciones del form de perfil. */
type ValidationLanguage = "en" | "es";

const VALIDATION_COPY: Record<
  ValidationLanguage,
  {
    aliasLength: string;
    telegramLength: string;
    birthDateMissing: string;
    minAge: (minAge: number) => string;
  }
> = {
  es: {
    aliasLength: "El alias debe tener entre 3 y 30 caracteres.",
    telegramLength: "El usuario de Telegram debe tener entre 3 y 50 caracteres.",
    birthDateMissing: "Ingresá tu fecha de nacimiento.",
    minAge: (minAge) => `Debes tener al menos ${minAge} años para continuar.`,
  },
  en: {
    aliasLength: "Nickname must be between 3 and 30 characters.",
    telegramLength: "Telegram handle must be between 3 and 50 characters.",
    birthDateMissing: "Please enter your date of birth.",
    minAge: (minAge) =>
      `You must be at least ${minAge} years old to continue.`,
  },
};

export function validateRegistrationData(
  input: RegistrationData,
  language: ValidationLanguage = "es",
): string | null {
  const copy = VALIDATION_COPY[language] ?? VALIDATION_COPY.es;
  const alias = (input.alias ?? "").trim();
  const telegram = (input.telegram ?? "").trim();
  const birthDate = (input.birthDate ?? "").trim();
  if (alias.length < 3 || alias.length > 30) {
    return copy.aliasLength;
  }
  if (telegram.length < 3 || telegram.length > 50) {
    return copy.telegramLength;
  }
  if (!birthDate) {
    return copy.birthDateMissing;
  }
  if (!isAdultBirthDate(birthDate)) {
    return copy.minAge(MIN_REGISTRATION_AGE);
  }
  return null;
}

// ─── Approved check ───────────────────────────────────────────────────────────

/**
 * Aprobación admin para acceso a contenido privado (Telegram link).
 *   - Admins → siempre aprobados.
 *   - publicMetadata.approved === true → aprobado.
 *   - resto → NO aprobado (default seguro para el flujo VIP).
 */
export function isUserApproved(user: UserResource | null | undefined): boolean {
  if (!user) return false;
  if (isClerkAdmin(user)) return true;
  return user.publicMetadata?.approved === true;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function getUserDisplayName(
  user: UserResource | null | undefined,
): string {
  if (!user) return "";
  const meta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
  const alias = typeof meta.alias === "string" ? meta.alias.trim() : "";
  if (alias) return alias;
  if (user.firstName) return user.firstName;
  return user.primaryEmailAddress?.emailAddress?.split("@")[0] ?? "";
}
